import {XYPosition} from 'reactflow';

import {assert, assertAndContinue} from 'Asserts';
import {ProcedureFlowchart, MethodNode, MethodEdge} from 'procedures/ProcedureDomainTypes';

import {extractMethodEdges} from '../flowchartManagementUtils';
import {COMMON_NODE_WIDTH, METHOD_BEING_DRAGGED_NODE_HEIGHT} from '../procedureMethodNodeCommonConsts';
import {COLUMN_GAP_PIXELS, ROW_GAP_PIXELS} from '../useConvertedGridLayoutGraph';
import {
  DropZoneType,
  MethodDropZone,
  MethodEdgeDropZone,
  MethodLeafDropZone,
  MethodRootDropZone,
} from './ProcedureMethodDragOverlayTypes';

export function buildLinearFlowchartMethodDropZones(
  flowchart: ProcedureFlowchart,
  nodeCidToComputedCanvasPos: {[nodeCid: string]: XYPosition},
  canvasPointToClientPoint: (pos: XYPosition) => XYPosition,
  maxHeightByRow: {[row: number]: number}
): Array<MethodDropZone> {
  // Due to useLayoutEffect in useConvertedGridLayoutGraph, nodeCidToComputedCanvasPos isn't always
  // up to date with the flowchart yet but will be on the next render
  // This condition avoids the main edge cases associated with the stale nodeCidToComputedCanvasPos
  if (Object.keys(nodeCidToComputedCanvasPos).length === 0 || flowchart.nodes.length === 0) {
    return [];
  }
  const methodEdges = extractMethodEdges(flowchart.edges);

  const methodCidsWithOutgoingEdges = methodEdges.map((edge) => edge.fromMethodCid);
  const leafNodes = flowchart.nodes.filter((node) => !methodCidsWithOutgoingEdges.includes(node.cid));
  if (!assertAndContinue(leafNodes.length === 1, 'Only linear flowcharts are supported')) {
    return [];
  }
  const leafNode = leafNodes[0];

  const methodCidsWithIncomingEdges = methodEdges.map((edge) => edge.toMethodCid);
  const rootNodes = flowchart.nodes.filter((node) => !methodCidsWithIncomingEdges.includes(node.cid));
  if (!assertAndContinue(rootNodes.length === 1, 'Only linear flowcharts are supported')) {
    return [];
  }
  const rootNode = rootNodes[0];

  /**
   * NodeIdToComputedCanvasPos is populated in a useLayoutEffect,
   * so when a new node is added there will be one render cycle where that mapping doesn't
   * have the new node yet because effect hooks run async - though flowchart.nodes will.
   *
   * Therefore, only calculate dropZones for methods (flowchart.nodes) also in NodeIdToComputedCanvasPos
   */
  const methodsWithOutgoingEdges = flowchart.nodes.filter(
    (node) =>
      methodCidsWithOutgoingEdges.includes(node.cid) &&
      Object.keys(nodeCidToComputedCanvasPos).includes(node.cid)
  );

  const methodEdgeDropZones = buildMethodEdgeDropZones(
    methodsWithOutgoingEdges,
    methodEdges,
    nodeCidToComputedCanvasPos,
    canvasPointToClientPoint,
    maxHeightByRow
  );
  let dropZones: Array<MethodDropZone> = methodEdgeDropZones;
  if (Object.keys(nodeCidToComputedCanvasPos).includes(rootNode.cid)) {
    dropZones = dropZones.concat(
      buildMethodRootDropZone(rootNode, nodeCidToComputedCanvasPos, canvasPointToClientPoint, maxHeightByRow)
    );
  }
  if (Object.keys(nodeCidToComputedCanvasPos).includes(leafNode.cid)) {
    dropZones = dropZones.concat(
      buildMethodLeafDropZone(leafNode, nodeCidToComputedCanvasPos, canvasPointToClientPoint, maxHeightByRow)
    );
  }

  return dropZones;
}

function buildMethodEdgeDropZones(
  methodsWithOutgoingEdges: Array<MethodNode>,
  methodEdges: Array<MethodEdge>,
  nodeIdToComputedCanvasPos: {[nodeId: string]: XYPosition},
  canvasPointToClientPoint: (pos: XYPosition) => XYPosition,
  maxHeightByRow: {[row: number]: number}
): Array<MethodEdgeDropZone> {
  return methodsWithOutgoingEdges.map((node) => {
    const computedCanvasPos = nodeIdToComputedCanvasPos[node.cid];
    const dropZoneTopLeftClientPos = canvasPointToClientPoint({
      x: computedCanvasPos.x + COMMON_NODE_WIDTH / 2,
      y: computedCanvasPos.y,
    });
    const dropZoneTopRightClientPos = canvasPointToClientPoint({
      x: computedCanvasPos.x + 1.5 * COMMON_NODE_WIDTH + COLUMN_GAP_PIXELS,
      y: computedCanvasPos.y,
    });
    const edge = methodEdges.find((edge) => edge.fromMethodCid === node.cid);
    assert(edge != null, 'Unexpectedly could not find related edge');
    const dropZone: MethodEdgeDropZone = {
      type: DropZoneType.METHOD_EDGE,
      position: dropZoneTopLeftClientPos,
      width: dropZoneTopRightClientPos.x - dropZoneTopLeftClientPos.x,
      height: maxHeightByRow[node.canvasGridCoordinates.row],
      edgeId: edge.id,
      fromMethod: node.cid,
      toMethod: edge.toMethodCid,
    };
    return dropZone;
  });
}

function buildMethodRootDropZone(
  rootNode: MethodNode,
  nodeIdToComputedCanvasPos: {[nodeId: string]: XYPosition},
  canvasPointToClientPoint: (pos: XYPosition) => XYPosition,
  maxHeightByRow: {[row: number]: number}
): MethodRootDropZone {
  const computedCanvasPos = nodeIdToComputedCanvasPos[rootNode.cid];
  const dropZoneTopLeftClientPos = canvasPointToClientPoint({
    x: computedCanvasPos.x - COMMON_NODE_WIDTH / 2 - COLUMN_GAP_PIXELS,
    y: computedCanvasPos.y,
  });
  const dropZoneTopRightClientPos = canvasPointToClientPoint({
    x: computedCanvasPos.x + COMMON_NODE_WIDTH / 2,
    y: computedCanvasPos.y,
  });
  return {
    type: DropZoneType.METHOD_ROOT,
    position: dropZoneTopLeftClientPos,
    width: dropZoneTopRightClientPos.x - dropZoneTopLeftClientPos.x,
    height: maxHeightByRow[rootNode.canvasGridCoordinates.row],
    prependToMethodCid: rootNode.cid,
  };
}

function buildMethodLeafDropZone(
  leafNode: MethodNode,
  nodeIdToComputedCanvasPos: {[nodeId: string]: XYPosition},
  canvasPointToClientPoint: (pos: XYPosition) => XYPosition,
  maxHeightByRow: {[row: number]: number}
): MethodLeafDropZone {
  const computedCanvasPos = nodeIdToComputedCanvasPos[leafNode.cid];
  const dropZoneTopLeftClientPos = canvasPointToClientPoint({
    x: computedCanvasPos.x + COMMON_NODE_WIDTH / 2,
    y: computedCanvasPos.y,
  });
  const dropZoneTopRightClientPos = canvasPointToClientPoint({
    x: computedCanvasPos.x + 1.5 * COMMON_NODE_WIDTH + COLUMN_GAP_PIXELS,
    y: computedCanvasPos.y,
  });
  return {
    type: DropZoneType.METHOD_LEAF,
    position: dropZoneTopLeftClientPos,
    width: dropZoneTopRightClientPos.x - dropZoneTopLeftClientPos.x,
    height: maxHeightByRow[leafNode.canvasGridCoordinates.row],
    appendFromMethodCid: leafNode.cid,
  };
}

export function doRectanglesOverlap(
  firstRect: {position: {x: number; y: number}; width: number; height: number},
  secondRect: {position: {x: number; y: number}; width: number; height: number}
): boolean {
  const [firstLeft, firstRight] = [firstRect.position.x, firstRect.position.x + firstRect.width];
  const [firstTop, firstBottom] = [firstRect.position.y, firstRect.position.y + firstRect.height];
  const [secondLeft, secondRight] = [secondRect.position.x, secondRect.position.x + secondRect.width];
  const [secondTop, secondBottom] = [secondRect.position.y, secondRect.position.y + secondRect.height];

  return (
    firstLeft <= secondRight &&
    firstRight >= secondLeft &&
    firstTop <= secondBottom &&
    firstBottom >= secondTop
  );
}

export function isPointInRectangle(
  rectangle: {position: {x: number; y: number}; width: number; height: number},
  pointPosition: {x: number; y: number}
): boolean {
  const [rectLeft, rectRight] = [rectangle.position.x, rectangle.position.x + rectangle.width];
  const [rectTop, rectBottom] = [rectangle.position.y, rectangle.position.y + rectangle.height];
  return (
    rectLeft <= pointPosition.x &&
    pointPosition.x <= rectRight &&
    rectTop <= pointPosition.y &&
    pointPosition.y <= rectBottom
  );
}

function getDropZoneIntersectionRectangle(dropZone: MethodDropZone): {
  position: {x: number; y: number};
  width: number;
  height: number;
} {
  return {
    position: {x: dropZone.position.x, y: dropZone.position.y - ROW_GAP_PIXELS / 2},
    width: dropZone.width,
    height: dropZone.height + ROW_GAP_PIXELS,
  };
}

export function detectDraggingNodeDropZoneIntersection(
  methodBeingDraggedPosition: {x: number; y: number},
  dropZones: Array<MethodDropZone>,
  mousePosition: {x: number; y: number},
  zoom: number
): MethodDropZone | null {
  const intersectingDropZones = dropZones.filter((dz) => {
    const dropZoneRectangle = getDropZoneIntersectionRectangle(dz);
    const methodBeingDraggedRectangle = {
      position: methodBeingDraggedPosition,
      width: COMMON_NODE_WIDTH * zoom,
      height: METHOD_BEING_DRAGGED_NODE_HEIGHT * zoom,
    };
    return doRectanglesOverlap(methodBeingDraggedRectangle, dropZoneRectangle);
  });

  if (intersectingDropZones.length === 0) {
    return null;
  }
  if (intersectingDropZones.length === 1) {
    return intersectingDropZones[0];
  }

  // When the dragging node intersects with multiple drop zones
  // Use mouse point coordinate as tie breaker
  const mouseIntersectDropZone = intersectingDropZones.find((dz) => {
    const dropZoneRectangle = getDropZoneIntersectionRectangle(dz);
    return isPointInRectangle(dropZoneRectangle, mousePosition);
  });

  return mouseIntersectDropZone ?? null;
}
